import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Doctor, PortalMode } from '../../types/api';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  beginHqSsoExchange,
  hqSsoLogoutUrl,
  hqSsoRedirectUri,
  HqSsoClientError,
  startHqSso,
} from '../../services/hqSso';

interface HqSsoCallbackProps {
  onLogin: (doctor: Doctor, needsSignature: boolean, portal?: PortalMode) => void;
}

const SOFT_OIDC_ERRORS = new Set(['login_required', 'interaction_required', 'consent_required']);

export default function HqSsoCallback({ onLogin }: HqSsoCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const oidcError = searchParams.get('error');
    if (oidcError && SOFT_OIDC_ERRORS.has(oidcError)) {
      // No IdP session (or re-auth needed): show login, do not sticky-error or force prompt=login.
      navigate('/login?sso=required', { replace: true });
      return;
    }
    if (oidcError) {
      navigate(`/login?sso=error&msg=${encodeURIComponent('HQ sign-in did not finish. Try again or sign out of HQ SSO.')}`, { replace: true });
      return;
    }

    let cancelled = false;
    const code = searchParams.get('code');
    beginHqSsoExchange(code, searchParams.get('state'), (pending) => apiService.completeHqSso({
      code: code ?? '',
      codeVerifier: pending.verifier,
      redirectUri: hqSsoRedirectUri(),
      clientId: 'doctor-consult-web',
    })).then(({ data, portal }) => {
      if (cancelled) return;
      const user = data.user;
      onLogin({
        id: user.id,
        name: user.fullName,
        mcr: user.mcr || '',
        displayName: user.displayName || user.fullName,
        customText1: user.customText1 || '',
        customText2: user.customText2 || '',
        maskOn: user.maskOn || false,
        code: user.code || '',
        username: user.username || '',
        roleId: user.roleId,
      }, portal === 'doctor', portal);
    }).catch((err: unknown) => {
      if (cancelled) return;
      setBusy(false);
      let message = getApiErrorMessage(err, 'HQ sign-in was not accepted.');
      let reason = 'error';
      if (err instanceof HqSsoClientError) {
        message = err.message;
        if (err.code === 'SSO_CODE_ALREADY_USED') {
          reason = 'used';
        } else if (err.code === 'SSO_STATE_MISMATCH') {
          // Stale PKCE race: do not sticky-error or force Singpass; allow normal HQ button / session reuse.
          navigate('/login?sso=required', { replace: true });
          return;
        }
      }
      // Leave the dead callback URL so refresh does not re-POST; land on login with recovery UI.
      navigate(`/login?sso=${reason}&msg=${encodeURIComponent(message)}`, { replace: true });
      setError(message);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, onLogin, searchParams]);

  if (!error && busy) {
    return (
      <div className="login-container">
        <div className="login-box">
          <p>Signing in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <p>{error || 'Signing in.'}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
          <button type="button" className="login-button" onClick={() => { void startHqSso({ prompt: 'login' }); }}>
            Try again
          </button>
          <button type="button" className="login-button" onClick={() => { window.location.assign(hqSsoLogoutUrl()); }}>
            Sign out of HQ SSO
          </button>
        </div>
      </div>
    </div>
  );
}